<template>
  <div class="night-card-settings px-4 py-6">
    <VCard class="pa-4">
      <template v-if="hasLinkedCard">
        <div
          class="d-flex align-items-center justify-space-between"
          :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }"
        >
          <div class="heading-6">{{ $t('nightRiderLink.nightRiderCardTitle') }}</div>
        </div>
        <div class="subtitle pt-4 mt-4">
          {{ $t('nightRiderLink.nightRiderCardSubtitle') }}
        </div>
        <div class="d-flex pt-4"
          :class="{
            'flex-column': $vuetify.breakpoint.smAndDown,
            'align-items-start': $vuetify.breakpoint.smAndDown,
            'justify-space-between': $vuetify.breakpoint.mdAndUp,
            'align-items-center': $vuetify.breakpoint.mdAndUp,
          }"
        >
          <div class="text-left">
            <span class="body-1">{{ profile.nightRiderCardNumber }}</span>
          </div>
          <div class="text-right">
            <VBtn
              class="w-100"
              color="brandingPrimary"
              text
              data-test="settings-night-card-replace"
              @click="startReplace"
            >
              <span class="button">{{ $t('nightRiderLink.settingsReplaceCard') }}</span>
            </VBtn>
          </div>
        </div>
        <VDivider />
        <div class="d-flex align-items-start" :class="{ 'py-4': !isLinkedCardExpired }">
          <img
            v-if="qrDataUrl && !isLinkedCardExpired"
            :src="qrDataUrl"
            width="180"
            height="180"
            alt=""
            class="mb-4"
          >
          <SInfoblock
            v-if="isLinkedCardExpired"
            color="error-color"
            class="info-block-expired-linked-night-card"
            :no-margin="true"
            data-test="linked-night-card-expired-message"
            :message="$t('nightRiderLink.expiredLinkedNightCardMessage')"
          />
        </div>
      </template>
      <template v-else>
        <div
          class="d-flex"
          :class="{
            'flex-column': $vuetify.breakpoint.smAndDown,
            'align-items-start': $vuetify.breakpoint.smAndDown,
            'justify-space-between': $vuetify.breakpoint.mdAndUp,
            'align-items-center': $vuetify.breakpoint.mdAndUp,
            'flex-column': $vuetify.breakpoint.smAndDown,
          }"
        >
          <div class="heading-6">{{ $t('nightRiderLink.settingsNoCardTitle') }}</div>
          <div v-if="infoLinkUrl" class="d-flex align-items-center">
            <VIcon class="secondary--text">
              mdi-help-circle-outline
            </VIcon>
            <a
              :href="infoLinkUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="body-2 secondary--text text-decoration-underline ml-2"
            >
              {{ $t('nightRiderLink.inviteLinkText') }}
            </a>
          </div>
        </div>
        <div
          class="d-flex mt-4"
          :class="{
            'flex-column': $vuetify.breakpoint.smAndDown,
            'align-items-start': $vuetify.breakpoint.smAndDown,
            'justify-space-between': $vuetify.breakpoint.mdAndUp,
            'align-items-center': $vuetify.breakpoint.mdAndUp,
          }"
        >
          <div class="text-left">
            <span class="body-1">{{ $t('nightRiderLink.settingsNoCardSubtitle') }}</span>
          </div>
          <div>
            <VBtn
              class="w-100"
              color="brandingPrimary"
              text
              data-test="checkout-add-night-card-link"
              @click="startAdd"
            >
              <span class="button">{{ $t('nightRiderLink.settingsAddCard') }}</span>
            </VBtn>
          </div>
        </div>
      </template>
    </VCard>

    <VDialog v-model="linkCardDialogOpen" max-width="600" scrollable>
      <VCard>
        <VCardTitle class="heading-6 pb-2 pt-4 px-4">
          {{ $t('nightRiderLink.checkoutAddCardDialogTitle') }}
        </VCardTitle>
        <VCardText class="pa-4 pt-0">
          <SNightRiderCardLinkForm
            ref="linkForm"
            :show-primary-action="false"
            :account-phone="profile.phone || ''"
            :info-link-url="infoLinkUrl"
            @submit="onSubmitLink"
          />
        </VCardText>
        <div
          class="pa-4 pt-0 d-flex flex-row flex-nowrap align-stretch night-card-settings-dialog-actions"
          :class="{
            'flex-column': $vuetify.breakpoint.smAndDown,
          }"
          style="width: 100%; column-gap: 8px;"
        >
          <div class="night-card-settings-dialog-actions__cell">
            <VBtn
              outlined
              class="w-100"
              data-test="settings-night-card-cancel"
              @click="cancelEdit"
            >
              <span class="button">{{ $t('nightRiderLink.inviteLater') }}</span>
            </VBtn>
          </div>
          <div class="night-card-settings-dialog-actions__cell">
            <VBtn
              color="brandingPrimary"
              dark
              class="w-100 text-decoration-none"
              :class="{
                'mt-2': $vuetify.breakpoint.smAndDown,
              }"
              data-test="settings-night-card-save"
              @click="onSaveEditClick"
            >
              <span class="button">{{ $t(isReplace ? 'nightRiderLink.saveCard' : 'nightRiderLink.settingsAddCard') }}</span>
            </VBtn>
          </div>
        </div>
      </VCard>
    </VDialog>
  </div>
</template>

<script lang="ts">
export default {
  name: 'SNightCardManagementPanel',
};
</script>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import SUserService from '@slg/web-customer-shared/src/services/UserService';
import { emitNightCardLinkError } from '@slg/web-customer-shared/src/utils/nightCardLinkErrorEvents';
import { withNightCardNameFallback } from '@slg/web-customer-shared/src/utils/nightCardLinkHelpers';
import { isLinkedNightCardExpired } from '@slg/web-customer-shared/src/utils/nightCardExpirationLogic';
import SInfoblock from '@slg/web-customer-shared/src/components/common/SInfoblock.vue';
import { useGlobalErrorDialog, useVm } from '../../composables';
import SNightRiderCardLinkForm from './SNightRiderCardLinkForm.vue';

const props = defineProps({
  profile: {
    type: Object,
    default: () => ({}),
  },
  infoLinkUrl: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['setGlobalLoading', 'populateUserProfileInfo']);

const { setGlobalError } = useGlobalErrorDialog();
const vm = useVm();

const linkCardDialogOpen = ref(false);
const isReplace = ref(false);
const qrDataUrl = ref('');
const linkForm = ref<any>(null);

const hasLinkedCard = computed(() => !!(props.profile as any)?.nightRiderCardNumber);
const isLinkedCardExpired = computed(() => (
  isLinkedNightCardExpired((props.profile as any)?.linkedNrCardExpirationAt)
));

async function refreshQr() {
  const num = (props.profile as any)?.nightRiderCardNumber;
  if (!num || isLinkedCardExpired.value) {
    qrDataUrl.value = '';
    return;
  }
  try {
    const QRCode = (await import('qrcode')).default;
    qrDataUrl.value = await QRCode.toDataURL(String(num), { width: 180, margin: 1 });
  } catch (_) {
    qrDataUrl.value = '';
  }
}

watch(
  () => (props.profile as any)?.nightRiderCardNumber,
  () => {
    refreshQr();
  },
  { immediate: true },
);

watch(
  () => (props.profile as any)?.linkedNrCardExpirationAt,
  () => {
    refreshQr();
  },
  { immediate: true },
);

watch(linkCardDialogOpen, (isOpen) => {
  if (!isOpen) {
    nextTick(() => {
      const f = linkForm.value;
      if (f && typeof f.reset === 'function') {
        f.reset();
      }
    });
  }
});

function openLinkCardDialog(replace: boolean) {
  isReplace.value = replace;
  linkCardDialogOpen.value = true;
  nextTick(() => {
    const f = linkForm.value;
    if (f && typeof f.reset === 'function') {
      f.reset();
    }
  });
}

function startAdd() {
  openLinkCardDialog(false);
}

function startReplace() {
  openLinkCardDialog(true);
}

function cancelEdit() {
  linkCardDialogOpen.value = false;
}

async function onSaveEditClick() {
  const form = linkForm.value;
  if (form && typeof form.submitFromParent === 'function') {
    await form.submitFromParent();
  }
}

async function onSubmitLink({ cardNumber, phone, firstName, lastName }: any) {
  emit('setGlobalLoading', true);
  try {
    await withNightCardNameFallback({
      firstName,
      lastName,
      request: ({ firstName: dtoFirstName, lastName: dtoLastName }: any) => SUserService.updateProfile({
        nightRiderCardDetailsFormDTO: {
          cardNumber,
          phone,
          firstName: dtoFirstName,
          lastName: dtoLastName,
        },
      }),
    });
    await emit('populateUserProfileInfo');
    linkCardDialogOpen.value = false;
  } catch (e: any) {
    if (e?.status === 400) {
      emitNightCardLinkError(vm, e);
    } else if (e?.status !== 401) {
      await setGlobalError(e);
    }
  }
  emit('setGlobalLoading', false);
}
</script>

<style lang="scss" scoped>
.night-card-settings {
  max-width: 612px;
  min-width: 612px;
  height: 177px;
  margin: 0 auto;

  .info-block-expired-linked-night-card {
    padding-top: 6px;
  }

  .text-left {
    max-width: 300px;
  }

  @media (max-width: 570px) {
    max-width: 100%;
    min-width: 100%;
    height: auto;

    .text-left {
      max-width: 100%;
    }
  }

  .subtitle {
    font-weight: 400;
    font-size: 14px;
    line-height: 150%;
    letter-spacing: 0.5px;
    vertical-align: middle;
    text-transform: uppercase;
    color: #64666B;
  }
}

.night-card-settings-dialog-actions {
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  box-sizing: border-box;
}

.night-card-settings-dialog-actions__cell {
  flex: 1 1 0;
  min-width: 0;
}
</style>
